// src/pages/ApplicationStatus.tsx: holding portal for applicants awaiting approval.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import {
  C, ManagerApplication, ApplicationTeamMember,
  jurisdictionLabel, fundTypeLabel, regStatusColor, REG_STATUS_LABELS, fmtDate, fmtDateTime,
  gbp, sendMail,
} from '../lib/applications';
import {
  generateContractHTML, generateInvoiceHTML, contractDataFromApp, invoiceDataFromApp,
  contractTotal, ContractSignature,
} from '../lib/generatePDF';
import { uploadDoc, fetchDocHtml } from '../lib/applicationDocs';
import DocumentModal from '../components/DocumentModal';
import OnboardingStatus from './OnboardingStatus';
import { findOwnApplication } from '../lib/onboardingApplications';

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

const STAGES = [
  { key: 'submitted',  label: 'Application Submitted' },
  { key: 'regulatory', label: 'Regulatory Verification' },
  { key: 'review',     label: 'Admin Review' },
  { key: 'contract',   label: 'Contract Sent' },
  { key: 'active',     label: 'Account Activated' },
];

const STATUS_MESSAGE: Record<string, string> = {
  submitted:        'Your application is being processed. We will begin regulatory verification shortly.',
  regulatory_check: 'We are verifying your regulatory registration. This usually takes a few minutes.',
  admin_review:     'Your application is under review by our team. We will be in touch within 2 business days.',
};

const ONBOARDING_KINDS = ['allocator', 'provider', 'candidate'] as const;

/**
 * One portal for all four applicant types. ?type= picks the view; without it,
 * a manager application is looked for first and any other type is discovered
 * from the applicant's own rows, so an emailed link with no parameter still
 * lands somewhere sensible.
 */
export default function ApplicationStatus() {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type') || '';

  if ((ONBOARDING_KINDS as readonly string[]).includes(typeParam)) {
    return <OnboardingStatus kind={typeParam as typeof ONBOARDING_KINDS[number]} />;
  }
  return <ManagerApplicationStatus />;
}

function ManagerApplicationStatus() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [app, setApp]         = useState<ManagerApplication | null>(null);
  const [team, setTeam]       = useState<ApplicationTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [otherKind, setOtherKind] = useState<'allocator' | 'provider' | 'candidate' | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('manager_applications').select('*')
        .order('created_at', { ascending: false }).limit(1).maybeSingle();
      if (cancelled) return;
      const row = (data as ManagerApplication) || null;
      setApp(row);
      if (row) {
        const { data: members } = await supabase.from('application_team_members')
          .select('*').eq('application_id', row.id).order('is_primary_contact', { ascending: false });
        if (!cancelled) setTeam((members as ApplicationTeamMember[]) || []);
      } else {
        // No manager application: the reader may have applied as one of the
        // other types without a ?type= parameter on the link.
        const found = await findOwnApplication();
        if (!cancelled && found) { setOtherKind(found.kind); }
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (otherKind) return <OnboardingStatus kind={otherKind} />;

  // Which timeline stages are complete for the current status.
  const stageState = (key: string): 'done' | 'current' | 'todo' => {
    if (!app) return 'todo';
    const s = app.status;
    const reg = app.regulatory_status;
    const order = ['submitted', 'regulatory_check', 'admin_review', 'contract_sent', 'contract_signed', 'approved'];
    const idx = order.indexOf(s);
    switch (key) {
      case 'submitted':  return 'done';
      case 'regulatory': return reg === 'verified' ? 'done' : s === 'regulatory_check' ? 'current' : idx >= 2 ? 'done' : 'todo';
      case 'review':     return idx >= 3 ? 'done' : s === 'admin_review' || s === 'more_info_requested' ? 'current' : 'todo';
      case 'contract':   return s === 'contract_signed' || s === 'approved' ? 'done' : s === 'contract_sent' ? 'current' : 'todo';
      // Signed is not yet active: an admin still has to activate the account.
      case 'active':     return s === 'approved' ? 'done' : s === 'contract_signed' ? 'current' : 'todo';
      default:           return 'todo';
    }
  };

  const reg = regStatusColor(app?.regulatory_status || null);

  return (
    <div style={{ minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ background: C.navy, padding: '18px 32px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
              <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#5dc9a8" strokeWidth="2.2" strokeLinecap="round" />
              <path d="M 6 14 A 8 8 0 0 0 22 14" stroke={C.gold} strokeWidth="2.2" strokeLinecap="round" />
              <circle cx="14" cy="14" r="2.2" fill={C.gold} />
            </svg>
            <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '18px', color: C.white }}>Altveno</span>
          </div>
          <button onClick={signOut} style={{
            background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '3px',
            color: 'rgba(255,255,255,0.75)', fontSize: '12px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui',
          }}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '28px 24px 64px' }}>
        {loading ? (
          <div style={{ ...card, padding: '48px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Loading your application…</div>
        ) : !app ? (
          <div style={{ ...card, padding: '44px', textAlign: 'center' }}>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: C.text, fontWeight: 400, margin: '0 0 10px' }}>No application found</h1>
            <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7, margin: '0 0 22px' }}>
              We could not find an application linked to this account.
            </p>
            <button onClick={() => navigate('/apply')} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: '3px', padding: '11px 24px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui' }}>
              Start an application
            </button>
          </div>
        ) : (
          <>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.text, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
              Your application
            </h1>
            <p style={{ fontSize: '13px', color: C.muted, margin: '0 0 20px' }}>{app.firm_name}</p>

            {app.status === 'more_info_requested' && (
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '3px', padding: '12px 15px', marginBottom: '16px', fontSize: '13px', color: C.amber, lineHeight: 1.6 }}>
                <strong>Additional information required</strong>, please check your email for details.
                {app.admin_notes && <div style={{ marginTop: '6px', color: C.textSec }}>{app.admin_notes}</div>}
              </div>
            )}

            {app.status === 'contract_sent' && (
              <div style={{ background: 'rgba(201,168,76,0.1)', border: `1px solid ${C.gold}66`, borderRadius: '3px', padding: '12px 15px', marginBottom: '16px', fontSize: '13px', color: C.amber, lineHeight: 1.6 }}>
                Your contract and invoice have been sent to <strong>{app.contact_email}</strong>. Review and sign the
                agreement below to activate your account.
              </div>
            )}

            {app.status === 'contract_signed' && (
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '3px', padding: '12px 15px', marginBottom: '16px', fontSize: '13px', color: C.success, lineHeight: 1.6 }}>
                <strong>Agreement signed{app.contract_signed_at ? ` on ${fmtDate(app.contract_signed_at)}` : ''}.</strong>{' '}
                Thank you. Your account will be activated within 1 business day.
              </div>
            )}

            {app.status === 'declined' && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '12px 15px', marginBottom: '16px', fontSize: '13px', color: C.danger, lineHeight: 1.6 }}>
                Your application was not successful on this occasion. Contact us at hello@altveno.com if you would like to discuss.
              </div>
            )}

            {STATUS_MESSAGE[app.status] && (
              <div style={{ ...card, padding: '13px 16px', marginBottom: '16px', fontSize: '13px', color: C.textSec, lineHeight: 1.6, borderLeft: `3px solid ${C.gold}` }}>
                {STATUS_MESSAGE[app.status]}
              </div>
            )}

            {/* ── Documents ── */}
            {['contract_sent', 'contract_signed', 'approved'].includes(app.status) && (
              <Documents app={app} onSigned={setApp} />
            )}

            {/* ── Timeline ── */}
            <div style={{ ...card, padding: '24px 26px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '18px' }}>Progress</div>
              {STAGES.map((stage, i) => {
                const state = stageState(stage.key);
                const colour = state === 'done' ? C.success : state === 'current' ? C.gold : C.border;
                return (
                  <div key={stage.key} style={{ display: 'flex', gap: '14px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                        background: state === 'todo' ? C.white : colour,
                        border: `1px solid ${colour}`, color: C.white,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: 700,
                      }}>
                        {state === 'done' ? '✓' : state === 'current' ? '•' : ''}
                      </div>
                      {i < STAGES.length - 1 && (
                        <div style={{ width: '1px', flex: 1, minHeight: '26px', background: state === 'done' ? C.success : C.border }} />
                      )}
                    </div>
                    <div style={{ paddingBottom: i < STAGES.length - 1 ? '16px' : 0 }}>
                      <div style={{
                        fontSize: '13.5px', marginTop: '2px',
                        fontWeight: state === 'current' ? 700 : 500,
                        color: state === 'todo' ? C.mutedLt : state === 'current' ? C.navy : C.text,
                      }}>{stage.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Summary ── */}
            <div style={{ ...card, padding: '24px 26px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '14px' }}>What you submitted</div>
              <SummaryRow k="Firm name" v={app.firm_name} />
              <SummaryRow k="Jurisdiction" v={jurisdictionLabel(app.jurisdiction)} />
              <SummaryRow k="Regulatory number" v={app.regulatory_number} />
              <SummaryRow k="Verification" v={
                <span style={{ background: reg.bg, color: reg.fg, border: `1px solid ${reg.bd}`, fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  {REG_STATUS_LABELS[app.regulatory_status || 'pending'] || app.regulatory_status}
                </span>
              } />
              {app.is_emerging_manager && <SummaryRow k="Emerging manager" v={`Yes${app.previous_firm_name ? `, previously ${app.previous_firm_name}` : ''}`} />}
              <SummaryRow k="Fund type" v={fundTypeLabel(app.fund_type)} />
              <SummaryRow k="Strategy" v={app.strategy} />
              <SummaryRow k="AUM range" v={app.aum_range} />
              <SummaryRow k="Submitted" v={fmtDate(app.created_at)} />

              {team.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '8px' }}>Team</div>
                  {team.map(m => (
                    <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px' }}>
                      <span style={{ color: C.text, fontWeight: 500 }}>{m.name}</span>
                      <span style={{ color: C.muted }}>{m.is_primary_contact ? 'Primary contact' : m.role}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Contact ── */}
            <div style={{ ...card, padding: '20px 24px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.text, marginBottom: '4px' }}>Questions about your application?</div>
              <a href="mailto:hello@altveno.com" style={{ fontSize: '13px', color: C.teal, textDecoration: 'none' }}>hello@altveno.com</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: '12px', padding: '7px 0', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px' }}>
      <div style={{ width: '160px', flexShrink: 0, color: C.muted }}>{k}</div>
      <div style={{ color: C.text, fontWeight: 500 }}>{v || '-'}</div>
    </div>
  );
}

// == Documents ================================================================

const docCard: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px',
  padding: '18px 20px', flex: '1 1 240px', minWidth: '240px',
  display: 'flex', flexDirection: 'column', gap: '10px',
};

const btnNavy: React.CSSProperties = {
  background: C.navy, color: C.gold, border: 'none', borderRadius: '3px',
  padding: '9px 16px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
  fontFamily: 'system-ui', textAlign: 'center', textDecoration: 'none', display: 'block',
};

const btnOutline: React.CSSProperties = {
  background: C.white, color: C.textSec, border: `1px solid ${C.border}`, borderRadius: '3px',
  padding: '8px 16px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
  fontFamily: 'system-ui', textAlign: 'center', textDecoration: 'none', display: 'block',
};

function DocIcon({ glyph }: { glyph: string }) {
  return (
    <div style={{
      width: '34px', height: '34px', borderRadius: '3px', background: C.navy, color: C.gold,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0,
    }}>{glyph}</div>
  );
}

function Documents({ app, onSigned }: {
  app: ManagerApplication; onSigned: (a: ManagerApplication) => void;
}) {
  const [signing, setSigning] = useState(false);
  const [viewing, setViewing] = useState<null | 'contract' | 'invoice' | 'signed'>(null);
  const [signedHtml, setSignedHtml] = useState<string | null>(null);
  const [loadingSigned, setLoadingSigned] = useState(false);

  // Generated from the application row rather than read back from storage: a
  // signed storage URL downloads rather than renders, and generating locally
  // means the document is always in step with the terms on the row.
  const contractHTML = useMemo(() => generateContractHTML(contractDataFromApp(app)), [app]);
  const invoiceHTML = useMemo(
    () => generateInvoiceHTML(invoiceDataFromApp(app, dueDateFor(app))),
    [app],
  );

  // The countersigned copy carries the drawn signature image, which lives only
  // in the stored file, so that one is fetched rather than regenerated.
  const openSigned = async () => {
    setViewing('signed');
    if (signedHtml) return;
    setLoadingSigned(true);
    setSignedHtml(await fetchDocHtml(app.id, 'contract-signed'));
    setLoadingSigned(false);
  };

  const isSigned = !!app.contract_signed_at;
  const total = contractTotal({
    annualFee: app.contract_price || 0,
    additionalFunds: Math.max(0, (app.fund_count || 1) - (app.funds_included || 1)),
    additionalFundPrice: app.additional_fund_price || 0,
  });

  return (
    <div style={{ marginBottom: '16px' }}>
      <h2 style={{
        fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '17px', color: C.navy,
        fontWeight: 400, margin: '0 0 12px',
      }}>Your Documents</h2>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

        {/* Platform Access Agreement */}
        <div style={docCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <DocIcon glyph="&#9998;" />
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.text, lineHeight: 1.3 }}>
                Platform Access Agreement
              </div>
              <span style={{
                display: 'inline-block', marginTop: '4px', padding: '2px 8px', borderRadius: '2px',
                fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
                color: isSigned ? C.success : C.amber,
                background: isSigned ? '#F0FDF4' : 'rgba(201,168,76,0.12)',
                border: `1px solid ${isSigned ? '#BBF7D0' : C.gold + '66'}`,
              }}>{isSigned ? 'Signed' : 'Ready to sign'}</span>
            </div>
          </div>

          {isSigned ? (
            <>
              <div style={{ fontSize: '12px', color: C.muted, lineHeight: 1.6 }}>
                Signed by {app.contract_signed_by_name || app.contact_name}
                {app.contract_signed_by_title ? `, ${app.contract_signed_by_title}` : ''}
                {app.contract_signed_at ? ` on ${fmtDateTime(app.contract_signed_at)}` : ''}.
              </div>
              <button onClick={openSigned} style={btnOutline}>View Signed Agreement</button>
            </>
          ) : (
            <div style={{ fontSize: '12px', color: C.muted, lineHeight: 1.6 }}>
              Read the agreement, then sign it here to activate your account.
            </div>
          )}

          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button onClick={() => setViewing('contract')} style={btnOutline}>View Agreement</button>
            {!isSigned && (
              <button onClick={() => setSigning(true)} style={btnNavy}>Sign Agreement</button>
            )}
          </div>
        </div>

        {/* Invoice */}
        <div style={docCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <DocIcon glyph="&#163;" />
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.text, lineHeight: 1.3 }}>
                Invoice {app.invoice_number || ''}
              </div>
              <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>
                {gbp(total)} due within 30 days
              </div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: C.muted, lineHeight: 1.6 }}>
            Payment details are on the invoice. Quote {app.invoice_number || 'the invoice number'} as your reference.
          </div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button onClick={() => setViewing('invoice')} style={btnOutline}>View Invoice</button>
          </div>
        </div>
      </div>

      {viewing === 'contract' && (
        <DocumentModal
          html={contractHTML}
          title={`Platform Access Agreement, ${app.firm_name}`}
          onClose={() => setViewing(null)}
        />
      )}
      {viewing === 'invoice' && (
        <DocumentModal
          html={invoiceHTML}
          title={`Invoice ${app.invoice_number || ''}`.trim()}
          onClose={() => setViewing(null)}
        />
      )}
      {viewing === 'signed' && (
        <DocumentModal
          html={signedHtml || fallbackDocHtml(loadingSigned
            ? 'Loading the signed agreement...'
            : 'The signed agreement could not be loaded. Contact hello@altveno.com.')}
          title={`Signed Agreement, ${app.firm_name}`}
          onClose={() => setViewing(null)}
        />
      )}

      {signing && (
        <SignatureModal
          app={app}
          onClose={() => setSigning(false)}
          onSigned={a => { setSigning(false); onSigned(a); }}
        />
      )}
    </div>
  );
}

/** Due date shown on the invoice: 30 days from when the contract was sent. */
function dueDateFor(app: ManagerApplication): string {
  const sent = app.contract_sent_at ? new Date(app.contract_sent_at) : new Date();
  return new Date(sent.getTime() + 30 * 86400000).toISOString().slice(0, 10);
}

/** A minimal document shown in the viewer while a stored file loads or fails. */
function fallbackDocHtml(message: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"></head>
<body style="font-family:system-ui,sans-serif;color:#6B7280;padding:60px;text-align:center;">
${message}
</body></html>`;
}

// == Signature modal ==========================================================

function SignatureModal({ app, onClose, onSigned }: {
  app: ManagerApplication; onClose: () => void; onSigned: (a: ManagerApplication) => void;
}) {
  const [name, setName]   = useState(app.contact_name || '');
  const [title, setTitle] = useState('');
  const [mode, setMode]   = useState<'draw' | 'type'>('draw');
  const [typed, setTyped] = useState('');
  const [hasDrawing, setHasDrawing] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing   = useRef(false);

  // Size the backing store to the device pixel ratio so strokes land under the
  // cursor rather than offset on a high-DPI screen.
  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = C.navy;
    setHasDrawing(false);
  }, [mode]);

  const pointAt = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const src = 'touches' in e ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = pointAt(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointAt(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawing(true);
  };

  const end = () => { drawing.current = false; };

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawing(false);
  }, []);

  const hasSignature = mode === 'draw' ? hasDrawing : !!typed.trim();
  const canSubmit = agreed && hasSignature && !!name.trim() && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr('');
    try {
      const signature: ContractSignature = {
        name: name.trim(),
        title: title.trim(),
        imageDataUrl: mode === 'draw' ? canvasRef.current?.toDataURL('image/png') : undefined,
        typedName: mode === 'type' ? typed.trim() : undefined,
        signedAt: new Date().toISOString(),
      };

      // The signed copy is the same generated agreement with the signature
      // block filled in, so the terms cannot drift between the two versions.
      const html = generateContractHTML(contractDataFromApp(app, signature));
      const path = await uploadDoc(app.id, 'contract-signed', html);

      // A security-definer function owns the transition: applicants hold no
      // UPDATE grant on manager_applications.
      const { data, error } = await supabase.rpc('sign_manager_application', {
        p_application_id: app.id,
        p_signer_name: signature.name,
        p_signer_title: signature.title,
        p_signed_doc_path: path,
      });
      if (error) throw error;

      await sendMail('contract_signed_admin', {
        firm_name: app.firm_name,
        signer_name: signature.name,
        signer_title: signature.title,
        signed_at: fmtDateTime(signature.signedAt),
        contact_email: app.contact_email,
      });

      onSigned((data as ManagerApplication)
        || { ...app, status: 'contract_signed', contract_signed_at: signature.signedAt });
    } catch (e: any) {
      setErr(e?.message || 'Your signature could not be saved. Please try again.');
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
    color: C.text, fontSize: '13px', padding: '9px 11px', outline: 'none',
    fontFamily: 'system-ui', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px',
    textTransform: 'uppercase', display: 'block', marginBottom: '5px',
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)', zIndex: 300,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '32px 20px', overflowY: 'auto',
      }}
    >
      <div style={{
        background: C.white, borderRadius: '4px', width: '100%', maxWidth: '560px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}` }}>
          <h3 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '19px', color: C.navy, fontWeight: 400, margin: '0 0 4px' }}>
            Sign Platform Access Agreement
          </h3>
          <div style={{ fontSize: '12.5px', color: C.muted, lineHeight: 1.6 }}>
            By signing below you agree to the terms of the Platform Access Agreement
            dated {fmtDate(app.contract_start_date || app.contract_sent_at)}.
          </div>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {err && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', marginBottom: '14px', fontSize: '12.5px', color: C.danger }}>
              {err}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Full name</label>
              <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Title / position</label>
              <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Managing Partner" />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Signature</label>
            <button
              onClick={() => { setMode(m => (m === 'draw' ? 'type' : 'draw')); setErr(''); }}
              style={{ background: 'none', border: 'none', color: C.teal, fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui', padding: 0 }}
            >
              {mode === 'draw' ? 'Type signature instead' : 'Draw signature instead'}
            </button>
          </div>

          {mode === 'draw' ? (
            <>
              <canvas
                ref={canvasRef}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                style={{
                  width: '100%', height: '140px', border: `1px solid ${C.border}`,
                  borderRadius: '3px', background: C.white, cursor: 'crosshair',
                  touchAction: 'none', display: 'block',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                <span style={{ fontSize: '11.5px', color: C.mutedLt }}>Draw your signature above.</span>
                <button onClick={clearCanvas} style={{ background: 'none', border: 'none', color: C.muted, fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui', padding: 0 }}>
                  Clear
                </button>
              </div>
            </>
          ) : (
            <>
              <input
                style={{
                  ...inputStyle, height: '80px',
                  fontFamily: '"Segoe Script", "Brush Script MT", "Snell Roundhand", cursive',
                  fontSize: '30px', color: C.navy, textAlign: 'center',
                }}
                value={typed} onChange={e => setTyped(e.target.value)} placeholder="Type your name"
              />
              <div style={{ fontSize: '11.5px', color: C.mutedLt, marginTop: '6px' }}>
                Your typed name will appear as your signature on the agreement.
              </div>
            </>
          )}

          <label style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', marginTop: '18px', cursor: 'pointer' }}>
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: '2px' }} />
            <span style={{ fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>
              I confirm I am authorised to sign on behalf of <strong>{app.firm_name}</strong> and I agree to
              the terms of the Platform Access Agreement.
            </span>
          </label>
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
          <button onClick={onClose} disabled={busy} style={btnOutline}>Cancel</button>
          <button onClick={submit} disabled={!canSubmit}
            style={{ ...btnNavy, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
            {busy ? 'Submitting...' : 'Submit Signature'}
          </button>
        </div>
      </div>
    </div>
  );
}
